/**
 * ปฏิทินเวรทั้งทีม — มุมมองทั้งเดือน: แกนตั้ง = พนักงาน, แกนนอน = วันที่ 1..สิ้นเดือน.
 *
 * ดึง roster ทั้งเดือนครั้งเดียว (useRoster(firstDay,lastDay)) แล้ว group by
 * emp_code+date ใน client — ไม่ยิง API ต่อคน. join shift_master เอาชื่อ/สีของกะ.
 * คอลัมน์ชื่อพนักงาน sticky ซ้าย, ตัวตารางเลื่อนแนวนอนได้.
 */
import { useState } from 'react';
import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEmployees, useMySubordinates, useShiftMaster, useRoster, useHolidays, useWorkSchedule, useOrgDepartments, useOrgPositions, useApprovedLeaves } from '../../hooks';
import { useAuth } from '../../hooks/useAuth';

const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const THAI_DOW = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']; // index = JS getDay (0=Sun)

// โทน Soft Gradient — ชมพู/พีช/เหลือง/เขียวอ่อน/ฟ้าอ่อน/ม่วงอ่อน วนกัน
const SHIFT_COLORS = [
  'bg-pink-100 text-pink-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-orange-100 text-orange-700',
];

type EmpLite = { emp_code: string; first_name: string; last_name: string; nickname?: string | null; department?: string | null; position?: string | null };

export function HRRosterCalendarContent() {
  const [cursor, setCursor] = useState(() => dayjs().startOf('month'));
  const [deptFilter, setDeptFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');

  const year = cursor.year();
  const firstDay = cursor.startOf('month');
  const lastDay = cursor.endOf('month');
  const from = firstDay.format('YYYY-MM-DD');
  const to = lastDay.format('YYYY-MM-DD');
  const daysInMonth = lastDay.date();

  // หัวหน้างาน (SUPERVISOR) → ดึงเฉพาะลูกทีมสายตัวเอง; OWNER/HR → ทั้งบริษัท
  const { profile } = useAuth();
  const isSupervisor = profile?.role === 'SUPERVISOR';
  const { data: allEmployees } = useEmployees(!isSupervisor) as { data: EmpLite[] | undefined };
  const { data: subData } = useMySubordinates(isSupervisor);
  const employees: EmpLite[] = isSupervisor
    ? (subData?.subordinates ?? []).map((s) => {
        const [first, ...rest] = (s.name ?? '').split(' ');
        return { emp_code: s.emp_code, first_name: first ?? s.name ?? s.emp_code, last_name: rest.join(' '), nickname: s.nickname, department: s.department, position: s.position };
      })
    : (allEmployees ?? []);
  const { data: orgDepts = [] } = useOrgDepartments();
  const { data: orgPositions = [] } = useOrgPositions();
  const { data: shifts } = useShiftMaster();
  const { data: roster } = useRoster(from, to);
  const { data: approvedLeaves } = useApprovedLeaves(from, to);
  const { data: holidays } = useHolidays(year) as { data: Array<{ date: string; name: string }> | undefined };
  const { data: schedule } = useWorkSchedule();

  // วันลาที่อนุมัติแล้ว: key `${emp_code}|${date}` → ป้ายเตือนกันลงเวรทับ
  const LEAVE_LABEL: Record<string, string> = { sick: 'ลาป่วย', personal: 'ลากิจ', vacation: 'ลาพักร้อน', maternity: 'ลาคลอด', ordination: 'ลาบวช' };
  const leaveByKey = new Map((approvedLeaves ?? []).map((l) => [`${l.emp_code}|${l.date}`, l.leave_type]));

  // ISO weekday (1=Mon..7=Sun) ของวันหยุดประจำสัปดาห์
  const weekendIso = new Set<number>(schedule?.weekend_days ?? [7]);
  // บังคับทำงานวันหยุดประเพณี → ไม่แสดงวันหยุดในปฏิทิน
  const holidayDates = new Set(schedule?.holiday_as_workday ? [] : (holidays ?? []).map((h) => h.date));

  // shift_id → { code/name, สี }
  const shiftById = new Map(
    (shifts ?? []).map((s, i) => [s.id, { row: s, color: SHIFT_COLORS[i % SHIFT_COLORS.length] }]),
  );

  // group roster: emp_code → (date → row) ใน client (ยิงครั้งเดียว)
  const rosterByEmp = new Map<string, Map<string, { shift_id: string | null }>>();
  for (const r of roster ?? []) {
    if (!rosterByEmp.has(r.emp_code)) rosterByEmp.set(r.emp_code, new Map());
    rosterByEmp.get(r.emp_code)!.set(r.date, { shift_id: r.shift_id });
  }

  const days = Array.from({ length: daysInMonth }, (_, i) => firstDay.add(i, 'day'));
  const list = (employees ?? []).filter((e) => {
    if (!e.emp_code) return false;
    if (deptFilter && (e.department ?? '') !== deptFilter) return false;
    if (positionFilter && (e.position ?? '') !== positionFilter) return false;
    return true;
  });

  // legend เฉพาะกะที่ปรากฏในเดือนนี้
  const usedShiftIds = new Set<string>();
  for (const r of roster ?? []) if (r.shift_id) usedShiftIds.add(r.shift_id);

  function isWeekendDay(d: dayjs.Dayjs): boolean {
    const iso = d.day() === 0 ? 7 : d.day(); // 1=Mon..7=Sun
    return weekendIso.has(iso);
  }

  return (
    <div className="space-y-3">
      {/* ตัวเลือกเดือน */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCursor((c) => c.subtract(1, 'month'))}
          className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
          title="เดือนก่อน"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="text-sm font-semibold text-ink w-32 text-center">
          {THAI_MONTHS[cursor.month()]} {year + 543}
        </div>
        <button
          onClick={() => setCursor((c) => c.add(1, 'month'))}
          className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50"
          title="เดือนถัดไป"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <button
          onClick={() => setCursor(dayjs().startOf('month'))}
          className="btn-secondary btn-slim ml-1"
        >
          เดือนนี้
        </button>
      </div>

      {/* Filter แผนก/ตำแหน่ง */}
      {(orgDepts.length > 0 || orgPositions.length > 0) && (
        <div className="grid grid-cols-2 gap-2 max-w-md">
          <select className="input" value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPositionFilter(''); }}>
            <option value="">ทุกแผนก</option>
            {orgDepts.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
          <select className="input" value={positionFilter} onChange={(e) => setPositionFilter(e.target.value)}>
            <option value="">ทุกตำแหน่ง</option>
            {orgPositions.filter((p) => { if (!deptFilter) return true; const did = orgDepts.find((d) => d.name === deptFilter)?.id ?? null; return p.department_id === did; }).map((p) => <option key={p.id} value={p.title}>{p.title}</option>)}
          </select>
        </div>
      )}

      {/* ตารางทั้งทีม */}
      <div className="card p-0 overflow-x-auto">
        {list.length === 0 ? (
          <p className="text-center text-warm-gray py-10 text-sm">ยังไม่มีพนักงาน</p>
        ) : (
          <table className="border-collapse text-xs">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-paper border-b border-r border-gray-200 px-3 py-2 text-left font-medium text-warm-gray min-w-[150px]">
                  พนักงาน
                </th>
                {days.map((d) => {
                  const we = isWeekendDay(d);
                  const ho = holidayDates.has(d.format('YYYY-MM-DD'));
                  return (
                    <th
                      key={d.format('YYYY-MM-DD')}
                      className={`border-b border-gray-100 px-1 py-1 text-center font-medium min-w-[34px] ${
                        ho ? 'bg-red-50 text-red-500' : we ? 'bg-gray-50 text-gray-400' : 'text-warm-gray'
                      }`}
                    >
                      <div className="leading-tight">{d.date()}</div>
                      <div className="text-[9px] leading-tight">{THAI_DOW[d.day()]}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {list.map((e) => {
                const byDate = rosterByEmp.get(e.emp_code);
                return (
                  <tr key={e.emp_code} className="hover:bg-gray-50/50">
                    <td className="sticky left-0 z-10 bg-white border-b border-r border-gray-200 px-3 py-1.5 align-middle min-w-[150px]">
                      <div className="text-ink truncate max-w-[140px]">
                        {e.first_name} {e.last_name}
                        {e.nickname ? ` (${e.nickname})` : ''}
                      </div>
                      <div className="text-[10px] text-warm-gray font-mono">{e.emp_code}</div>
                    </td>
                    {days.map((d) => {
                      const iso = d.format('YYYY-MM-DD');
                      const we = isWeekendDay(d);
                      const ho = holidayDates.has(iso);
                      const cell = byDate?.get(iso);
                      const shift = cell && cell.shift_id ? shiftById.get(cell.shift_id) : null;
                      const leaveType = leaveByKey.get(`${e.emp_code}|${iso}`);

                      // ลาอนุมัติแล้ว → ป้าย "ลา" (กันลงเวรทับ); มีกะ → โค้ดกะ/สี; อื่น → จุดเทา
                      let inner;
                      if (leaveType) {
                        inner = (
                          <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-medium bg-rose-100 text-rose-700 ${shift ? 'ring-1 ring-red-400' : ''}`}>
                            ลา
                          </span>
                        );
                      } else if (shift) {
                        inner = (
                          <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-medium ${shift.color}`}>
                            {shift.row.code}
                          </span>
                        );
                      } else {
                        inner = <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-200" title="หยุด" />;
                      }

                      return (
                        <td
                          key={iso}
                          className={`border-b border-gray-50 text-center px-0.5 py-1 ${
                            ho ? 'bg-red-50/40' : we ? 'bg-gray-50/60' : ''
                          }`}
                          title={
                            leaveType
                              ? `${e.first_name} · ${d.format('D MMM')} · ${LEAVE_LABEL[leaveType] ?? 'ลา'} (อนุมัติแล้ว)${shift ? ' ⚠️ มีกะทำงานทับวันลา' : ''}`
                              : shift
                              ? `${e.first_name} · ${d.format('D MMM')} · ${shift.row.name} (${shift.row.start_time}–${shift.row.end_time})`
                              : `${e.first_name} · ${d.format('D MMM')} · หยุด`
                          }
                        >
                          {inner}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* legend */}
      <div className="flex items-center gap-3 flex-wrap text-[11px] text-warm-gray">
        {(shifts ?? [])
          .filter((s) => usedShiftIds.has(s.id))
          .map((s) => {
            const c = shiftById.get(s.id);
            return (
              <span key={s.id} className="flex items-center gap-1">
                <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-medium ${c?.color ?? ''}`}>
                  {s.code}
                </span>
                {s.name}
              </span>
            );
          })}
        <span className="flex items-center gap-1">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-200" /> หยุด
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block rounded px-1 text-[10px] font-medium bg-rose-100 text-rose-700">ลา</span> ลาอนุมัติแล้ว (ลงเวรทับไม่ได้)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-red-50 border border-red-200" /> วันหยุด
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-gray-50 border border-gray-200" /> หยุดประจำสัปดาห์
        </span>
      </div>
      <p className="text-[11px] text-warm-gray">เลื่อนตารางแนวนอนเพื่อดูทั้งเดือน · แตะช่องเพื่อดูรายละเอียดกะ</p>
    </div>
  );
}
